import { Botao } from '../../components/Botao'
import { AvisoDeErro } from '../../components/Estados'
import { Explicacao } from '../../components/Explicacao'
import { TEXTOS } from '../../lib/glossario'
import { useConferirOMes } from '../../api/queries/iad'
import type { ConferenciaDoIad } from '../../types/iad'
import { instantePorExtenso } from './pontuacao'

/**
 * O ato que destrava a apuração do indicador.
 *
 * **É a única coisa que separa "mês limpo" de "mês que ninguém olhou".** Os outros cinco
 * indicadores se apuram sobre chamados que já estão no sistema: sem chamado, o próprio denominador
 * vazio conta a história. O IAD não tem fonte nenhuma além do fiscal — e a meta dele é quanto menor
 * melhor, de modo que zero ponto é a leitura de conformidade perfeita. Sem este botão, a tela
 * afirmaria conformidade que ninguém verificou.
 *
 * **Quem só consulta lê o aviso e não vê o botão.** Declarar que o mês foi varrido é ato do fiscal,
 * e o texto acima continua explicando por que o indicador está sem número — que é a informação.
 *
 * @param props o mês, a conferência já registrada quando houver, e se a conta pratica o ato
 * @returns o aviso ou o registro do ato
 */
export function ConferenciaDoMes({
  competencia,
  conferencia,
  podeAgir,
}: {
  competencia: string
  conferencia: ConferenciaDoIad | null
  podeAgir: boolean
}) {
  const t = TEXTOS.iad
  const conferir = useConferirOMes()

  if (conferencia) {
    return (
      <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-900 ring-1 ring-inset ring-emerald-200">
        {t.conferidoPor(conferencia.conferidaPorNome, instantePorExtenso(conferencia.conferidaEm))}
      </p>
    )
  }

  return (
    <section className="cartao space-y-3 p-4" aria-label={t.naoConferido.titulo}>
      <div className="flex items-center gap-1.5">
        <h2 className="text-base font-semibold text-slate-900">{t.naoConferido.titulo}</h2>
        <Explicacao termo="conferenciaDoMes" />
      </div>
      <p className="max-w-prose text-sm text-slate-600">{t.naoConferido.ajuda}</p>

      {conferir.isError && <AvisoDeErro erro={conferir.error} />}

      {podeAgir ? (
        <Botao aparencia="primario" onClick={() => conferir.mutate(competencia)} disabled={conferir.isPending}>
          {t.naoConferido.botao}
        </Botao>
      ) : (
        <p className="max-w-prose text-sm text-slate-500">{TEXTOS.somenteConsulta}</p>
      )}
    </section>
  )
}
